import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Menu, X, Phone, Mail, ChevronRight, 
  Facebook, Youtube 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import schoollogo from '../assets/logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // --- OFFICIAL CONTACT & SOCIAL DATA ---
  const contactInfo = {
    phone: "+20 10 09640334",
    email: "maged.khalil@stemksheikh.moe.edu.eg"
  };

  const socialLinks = [
    { icon: Facebook, url: "https://www.facebook.com/share/g/18Ao15ZRRG/", label: "Facebook" },
    { icon: Youtube, url: "https://youtube.com/@kafrelsheikhstem4180?si=jUwBiuDW4gBD7ScD", label: "Youtube" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Staff', path: '/staff' },
    { name: 'Achievements', path: '/achievements' },
    { name: 'Campus', path: '/campus' },
  ];

  const isActive = (path) => location.pathname === path;

  // Animation Variants
  const menuVariants = {
    closed: { x: "100%", transition: { type: "spring", stiffness: 300, damping: 30 } },
    opened: { x: 0, transition: { type: "spring", stiffness: 300, damping: 30, staggerChildren: 0.1, delayChildren: 0.2 } }
  };

  const itemVariants = {
    closed: { opacity: 0, x: 20 },
    opened: { opacity: 1, x: 0 }
  };

  return (
    <>
      {/* --- TOP UTILITY BAR (Desktop) --- */}
      <div className="hidden lg:block bg-gray-50 border-b border-gray-100 py-2">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center text-[11px] font-bold text-[#1e3a8a]">
          <div className="flex items-center space-x-3">
            {socialLinks.map((social, i) => (
              <a 
                aria-label={`Join our ${social.label} Community`}
                key={i} 
                href={social.url} 
                target="_blank" 
                rel="noreferrer"
                className="bg-[#1e3a8a] text-white p-1.5 rounded-full hover:bg-[#8B0000] transition-all flex items-center justify-center min-w-[28px] min-h-[28px]"
              >
                <social.icon size={12} />
              </a>
            ))}
          </div>

          <div className="flex items-center space-x-8">
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-[#8B0000]" />
              <div className="flex flex-col">
                {/* Fixed text color from gray-400 to slate-600 for contrast */}
                <span className="text-[8px] text-slate-600 uppercase leading-none mb-1">Official Email</span>
                <a href={`mailto:${contactInfo.email}`} className="hover:text-[#8B0000] transition-colors">{contactInfo.email}</a>
              </div>
            </div>
            <div className="flex items-center gap-2 border-l border-gray-200 pl-8">
              <Phone size={14} className="text-[#8B0000]" />
              <div className="flex flex-col">
                {/* Fixed text color from gray-400 to slate-600 for contrast */}
                <span className="text-[8px] text-slate-600 uppercase leading-none mb-1">Direct Line</span>
                <a href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`} className="hover:text-[#8B0000] transition-colors">{contactInfo.phone}</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- MAIN NAVBAR --- */}
      <nav className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-2' : 'bg-white py-4'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            
            <Link to="/" className="flex items-center gap-3 group" aria-label="Kafr El-Sheikh STEM School Portal Home">
              <img 
                src={schoollogo} 
                alt="Kafr El-Sheikh STEM School Crest Logo" 
                width="56"
                height="56"
                className="h-14 transition-all duration-500" 
              />
              <div className="flex flex-col">
                <h1 className="text-[#1e3a8a] font-black text-xl leading-none tracking-tighter uppercase italic group-hover:text-[#8B0000] transition-colors">Kafr Elsheikh</h1>
                {/* Fixed text color from gray-400 to slate-500 for contrast */}
                <p className="text-slate-500 text-[9px] font-bold tracking-[0.3em] uppercase">STEM School</p>
              </div>
            </Link>

            {/* Desktop Links */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link key={link.name} to={link.path} className="relative py-2 group" aria-label={`Maps to ${link.name} page`}>
                  <span className={`text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${
                    isActive(link.path) ? 'text-[#8B0000]' : 'text-[#1e3a8a] group-hover:text-[#8B0000]'
                  }`}>
                    {link.name}
                  </span>
                  <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-1 bg-[#8B0000] transition-all duration-300 rounded-full ${
                    isActive(link.path) ? 'w-4' : 'w-0 group-hover:w-4'
                  }`} />
                </Link>
              ))}
              <Link to="/contact" aria-label="Navigate to school contact portal">
                <button className="bg-[#1e3a8a] text-white px-8 py-3 rounded-full shadow-lg hover:bg-[#8B0000] transition-all font-black text-[10px] tracking-[0.2em] flex items-center gap-2 uppercase min-h-[44px]">
                  Contact <ChevronRight size={14} />
                </button>
              </Link>
            </div>

            {/* Mobile Toggle */}
            <button 
              onClick={() => setIsOpen(true)} 
              className="lg:hidden p-3 text-[#1e3a8a] hover:bg-gray-50 rounded-full transition-colors min-w-[48px] min-h-[48px] flex items-center justify-center"
              aria-label="Open Navigation Menu"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>
      </nav>

      {/* --- PREMIUM MOBILE OVERLAY --- */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={menuVariants}
            initial="closed"
            animate="opened"
            exit="closed"
            className="fixed inset-0 z-[100] bg-white flex flex-col w-full h-full"
          >
            {/* Mobile Menu Header */}
            <div className="flex justify-between items-center px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <img src={schoollogo} alt="School Emblem" width="40" height="40" className="h-10" />
                <span className="text-[#1e3a8a] font-black text-sm tracking-tighter uppercase leading-none">
                  Kafr Elsheikh <br/><span className="text-[9px] text-[#8B0000] font-bold tracking-widest uppercase">STEM School</span>
                </span>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-3 text-[#1e3a8a] hover:bg-gray-50 rounded-full transition-colors min-w-[48px] min-h-[48px] flex items-center justify-center"
                aria-label="Close Navigation Menu"
              >
                <X size={30} />
              </button>
            </div>

            {/* Mobile Nav Links */}
            <div className="flex-grow flex flex-col justify-center px-10 space-y-8">
              {navLinks.map((link) => (
                <motion.div key={link.name} variants={itemVariants}>
                  <Link 
                    to={link.path} 
                    onClick={() => setIsOpen(false)}
                    aria-label={`Open ${link.name} page`}
                    className={`text-4xl font-black uppercase tracking-tighter transition-colors block py-2 ${
                      isActive(link.path) ? 'text-[#8B0000] italic underline decoration-4' : 'text-[#1e3a8a] hover:text-[#8B0000]'
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              
              <motion.div variants={itemVariants}>
                <Link 
                  to="/contact" 
                  onClick={() => setIsOpen(false)}
                  aria-label="Open School Contact Form"
                  className="inline-flex items-center gap-3 bg-[#1e3a8a] text-white px-10 py-5 rounded-full font-black text-lg uppercase tracking-widest shadow-2xl active:scale-95 transition-all min-h-[48px]"
                >
                  Contact Us <ChevronRight size={20} />
                </Link>
              </motion.div>
            </div>

            {/* Mobile Menu Footer */}
            <div className="bg-gray-50 p-10 space-y-8">
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-[#1e3a8a]">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm text-[#8B0000] shrink-0">
                    <Phone size={18} />
                  </div>
                  <a href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`} className="font-black text-sm hover:text-[#8B0000] transition-colors">{contactInfo.phone}</a>
                </div>
                <div className="flex items-center gap-4 text-[#1e3a8a]">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm text-[#8B0000] shrink-0">
                    <Mail size={18} />
                  </div>
                  <a href={`mailto:${contactInfo.email}`} className="font-black text-xs truncate hover:text-[#8B0000] transition-colors">{contactInfo.email}</a>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-6 border-t border-gray-200">
                {socialLinks.map((social, i) => (
                  <a 
                    aria-label={`Join our official ${social.label} channel`}
                    key={i} 
                    href={social.url} 
                    target="_blank"
                    rel="noreferrer"
                    className="w-12 h-12 rounded-xl bg-[#1e3a8a] text-white flex items-center justify-center hover:bg-[#8B0000] transition-all shadow-lg min-w-[48px] min-h-[48px]"
                  >
                    <social.icon size={20} />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;